import React, { useEffect } from 'react';
import OverviewTab from './CandidateTabs/OverviewTab';
import MultiPerspectiveTab from './CandidateTabs/MultiPerspectiveTab';
import AssessmentTab from './CandidateTabs/AssessmentTab';
import DecisionIntelTab from './CandidateTabs/DecisionIntelTab';
import InterviewTab from './CandidateTabs/InterviewTab';
import HumanInTheLoopTab from './CandidateTabs/HumanInTheLoopTab';
import ExplainabilityTab from './CandidateTabs/ExplainabilityTab';
import './CandidateTabs.css';

/** Hide Capability tab on candidate detail (UI temporarily disabled). */
const SHOW_CAPABILITY_TAB = false;

const CandidateTabs = ({ candidate, activeTab, onTabChange }) => {
    // #region agent log
    useEffect(() => {
        console.log('[DEBUG] activeTab prop changed', { activeTab, candidateId: candidate?.id, timestamp: Date.now() });
    }, [activeTab, candidate?.id]);
    // #endregion
    // #region agent log
    console.log('[DEBUG] CandidateTabs rendered', { activeTabProp: activeTab, candidateId: candidate?.id });
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateTabs.js:11',message:'CandidateTabs rendered',data:{activeTabProp:activeTab,candidateId:candidate?.id},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    // Decision Lenses - tabs that answer specific decision questions
    const tabs = [
        { id: 'summary', label: 'Summary' },
        ...(SHOW_CAPABILITY_TAB ? [{ id: 'capability', label: 'Capability' }] : []),
        { id: 'fit', label: 'Fit' },
        { id: 'interview', label: 'Interview' },
        { id: 'human-in-the-loop', label: 'Human In the Loop' },
        { id: 'explainability', label: 'Explainability' }
    ];

    // Map old tab IDs to new ones for backward compatibility
    const normalizeTabId = (tabId) => {
        if (!tabId) return 'summary';
        const mapping = {
            'overview': 'summary',
            'summary': 'summary', // Already new format
            'assessment': 'capability',
            'capability': 'capability', // Already new format
            'perspective': 'fit',
            'fit': 'fit', // Already new format
            'human-in-the-loop': 'human-in-the-loop', // Human In the Loop tab
            'explainability': 'explainability',
            'interview': 'interview'
        };
        let normalized = mapping[tabId] || tabId || 'summary';
        if (!SHOW_CAPABILITY_TAB && (normalized === 'capability' || normalized === 'assessment')) {
            normalized = 'summary';
        }
        // #region agent log
        console.log('[DEBUG] normalizeTabId', { inputTabId: tabId, normalizedTabId: normalized, wasInMapping: !!mapping[tabId] });
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateTabs.js:normalizeTabId',message:'Tab ID normalized',data:{inputTabId:tabId,normalizedTabId:normalized,wasInMapping:!!mapping[tabId]},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'C'})}).catch(()=>{});
        // #endregion
        return normalized;
    };

    const currentTab = normalizeTabId(activeTab);
    // #region agent log
    console.log('[DEBUG] Current tab determined', { activeTabProp: activeTab, currentTab, isHumanInLoop: currentTab === 'human-in-the-loop' });
    fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateTabs.js:39',message:'Current tab determined',data:{activeTabProp:activeTab,currentTab,isHumanInLoop:currentTab==='human-in-the-loop'},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'D'})}).catch(()=>{});
    // #endregion

    const renderTabContent = () => {
        let renderedComponent = null;
        let caseMatched = '';
        
        switch (currentTab) {
            case 'summary':
                caseMatched = 'summary';
                renderedComponent = <OverviewTab candidate={candidate} />;
                break;
            case 'capability':
                if (!SHOW_CAPABILITY_TAB) {
                    caseMatched = 'summary';
                    renderedComponent = <OverviewTab candidate={candidate} />;
                    break;
                }
                caseMatched = 'capability';
                renderedComponent = <AssessmentTab candidate={candidate} />;
                break;
            case 'fit':
                caseMatched = 'fit';
                renderedComponent = <MultiPerspectiveTab candidate={candidate} />;
                break;
            case 'interview':
                caseMatched = 'interview';
                renderedComponent = <InterviewTab candidate={candidate} />;
                break;
            case 'human-in-the-loop':
                caseMatched = 'human-in-the-loop';
                renderedComponent = <HumanInTheLoopTab candidate={candidate} />;
                break;
            case 'explainability':
                caseMatched = 'explainability';
                renderedComponent = <ExplainabilityTab candidate={candidate} />;
                break;
            default:
                caseMatched = 'default';
                renderedComponent = <OverviewTab candidate={candidate} />;
        }
        
        // #region agent log
        console.log('[DEBUG] Tab content rendered', { currentTab, caseMatched, isHumanInLoop: caseMatched === 'human-in-the-loop' });
        fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateTabs.js:renderTabContent',message:'Tab content rendered',data:{currentTab,caseMatched,isHumanInLoop:caseMatched==='human-in-the-loop'},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'E'})}).catch(()=>{});
        // #endregion
        
        return renderedComponent;
    };

    return (
        <div className="candidate-tabs-container">
            <div className="candidate-tabs-header">
                <div className="tabs-rail">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`decision-lens-tab ${currentTab === tab.id ? 'active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // #region agent log
                                console.log('[DEBUG] Tab button clicked', { 
                                    clickedTabId: tab.id, 
                                    tabLabel: tab.label, 
                                    currentTabBeforeClick: currentTab, 
                                    isHumanInLoop: tab.id === 'human-in-the-loop',
                                    onTabChangeType: typeof onTabChange,
                                    onTabChangeExists: !!onTabChange
                                });
                                fetch('http://127.0.0.1:7242/ingest/f0c51a36-2f20-40c5-b0fa-37899de60541',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'CandidateTabs.js:onClick',message:'Tab button clicked',data:{clickedTabId:tab.id,tabLabel:tab.label,currentTabBeforeClick:currentTab,isHumanInLoop:tab.id==='human-in-the-loop'},timestamp:Date.now(),sessionId:'debug-session',runId:'tab-debug',hypothesisId:'B'})}).catch(()=>{});
                                // #endregion
                                if (onTabChange && typeof onTabChange === 'function') {
                                    console.log('[DEBUG] Calling onTabChange with:', tab.id);
                                    onTabChange(tab.id);
                                } else {
                                    console.error('[DEBUG] onTabChange is not a function!', { onTabChange });
                                }
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="candidate-tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default CandidateTabs;
